// =====================================================
// PRICE PREVIEW CARD
// Stage 2: Participants & Pricing
// Shows pricing calculation preview
// =====================================================

import { formatBs } from '@/lib/money';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingDown, DollarSign, Tag } from 'lucide-react';
import { eventParticipantsRepository } from '@/data/eventParticipantsRepository';

// ===================
// TYPES
// ===================

interface PricePreviewCardProps {
  eventId: string;
  ministerId?: string;
  guestId?: string;
  /** Preview for a guest not created yet (no id) — guest-agnostic rules only */
  asGuest?: boolean;
  basePrice: number;
}

// ===================
// COMPONENT
// ===================

export function PricePreviewCard({ eventId, ministerId, guestId, asGuest, basePrice }: PricePreviewCardProps) {
  // Fetch price calculation
  const { data: priceResult, isLoading } = useQuery({
    queryKey: ['priceCalculation', eventId, ministerId, guestId, asGuest],
    queryFn: () =>
      eventParticipantsRepository.calculateParticipantPrice(eventId, basePrice, ministerId, guestId),
    enabled: !!ministerId || !!guestId || !!asGuest,
    staleTime: 1 * 60 * 1000, // 1 minute
  });

  // Show loading state
  if (isLoading) {
    return (
      <Card className="border-muted">
        <CardContent className="space-y-3 py-6">
          <div className="animate-pulse space-y-2">
            <div className="h-4 w-24 rounded bg-muted" />
            <div className="h-6 w-32 rounded bg-muted" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show base price if no minister/guest selected
  if (!ministerId && !guestId && !asGuest) {
    return (
      <Card className="border-muted">
        <CardContent className="space-y-3 py-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <DollarSign className="size-4" />
            <span>Precio base</span>
          </div>
          <div className="text-2xl font-bold">{formatBs(basePrice)}</div>
        </CardContent>
      </Card>
    );
  }

  // Show calculation result
  return (
    <Card className={priceResult?.applied_rule ? 'border-green-500/50 bg-green-50/50' : 'border-muted'}>
      <CardContent className="space-y-4 py-6">
        {/* Base Price */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <DollarSign className="size-4" />
            <span>Precio base</span>
          </div>
          <span className="font-medium">{formatBs(priceResult?.base_price || basePrice)}</span>
        </div>

        {/* Applied Rule (if any) */}
        {priceResult?.applied_rule && (
          <>
            <div className="flex items-center gap-2 border-t pt-3">
              <Tag className="size-4 text-green-600" />
              <div className="flex-1">
                <div className="text-sm font-medium text-green-700">
                  {priceResult.applied_rule.name}
                </div>
                {priceResult.applied_rule.description && (
                  <div className="text-xs text-muted-foreground">
                    {priceResult.applied_rule.description}
                  </div>
                )}
              </div>
            </div>

            {/* Discount */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-green-700">
                <TrendingDown className="size-4" />
                <span>Descuento</span>
              </div>
              <span className="font-medium text-green-700">
                -{formatBs(priceResult.discount_amount)}{' '}
                <span className="text-xs">({priceResult.discount_percentage.toFixed(0)}%)</span>
              </span>
            </div>
          </>
        )}

        {/* No discount message */}
        {!priceResult?.applied_rule && (
          <div className="border-t pt-3 text-center">
            <p className="text-xs text-muted-foreground">Sin descuentos aplicables</p>
          </div>
        )}

        {/* Final Price */}
        <div className="flex items-center justify-between border-t pt-3">
          <span className="text-sm font-medium">Precio Final</span>
          <span className="text-2xl font-bold text-primary">
            {formatBs(priceResult?.final_price || basePrice)}
          </span>
        </div>

        {/* Calculation Details */}
        {priceResult?.calculation_details && (
          <div className="rounded-md bg-muted/50 px-3 py-2">
            <p className="text-xs text-muted-foreground">{priceResult.calculation_details}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
